import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Sparkles, ExternalLink, Maximize2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import FullscreenImageViewer from "@/components/FullscreenImageViewer";
import { getAdsPool } from "@/lib/adsPool";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Small muted video thumb used when an ad has video_url instead of image_url. */
function AdVideoThumb({ src, className }) {
  return (
    <video src={src} className={`${className} object-cover bg-black flex-shrink-0`} muted loop playsInline autoPlay preload="metadata" data-testid="sponsored-video-thumb" />
  );
}

/**
 * SponsoredAdWidget — global sponsored/house ad slot.
 * Fetches the full pool of approved sponsored ads and rotates through them
 * automatically so every page keeps surfacing fresh creatives, always with a
 * "Patrocinado" tag.
 *
 * Props:
 *  - variant: "card" | "inline" | "compact" | "banner"
 *  - limit:   number of ads visible at once (window size into the pool)
 *  - rotateEvery: ms between rotations (default 7000). Set 0 to disable.
 *  - className
 */
export default function SponsoredAdWidget({
  variant = "card",
  limit = 1,
  rotateEvery = 7000,
  className = "",
}) {
  const [pool, setPool] = useState([]);
  const [cursor, setCursor] = useState(0);
  const [zoomSrc, setZoomSrc] = useState(null);
  const seenRef = useRef(new Set());

  // Fetch pool once (shared cache across all widget instances).
  useEffect(() => {
    let mounted = true;
    getAdsPool()
      .then((data) => {
        if (!mounted) return;
        const all = (data || []).filter((a) => (a.image_url || a.video_url || a.description || a.title));
        if (!all.length) return;
        const sponsored = all.filter((a) => a.sponsored);
        const rest      = all.filter((a) => !a.sponsored);
        const shuffle = (arr) => {
          for (let i = arr.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [arr[i], arr[j]] = [arr[j], arr[i]];
          }
        };
        shuffle(sponsored);
        shuffle(rest);
        setPool([...sponsored, ...rest]);
        setCursor(0);
      });
    return () => { mounted = false; };
  }, []);

  // Auto-rotate the visible window through the pool.
  useEffect(() => {
    if (!rotateEvery || pool.length <= limit) return; // nothing to rotate through
    const iv = setInterval(() => {
      setCursor((c) => (c + limit) % pool.length);
    }, rotateEvery);
    return () => clearInterval(iv);
  }, [pool.length, limit, rotateEvery]);

  const visible = useMemo(() => {
    if (!pool.length) return [];
    const out = [];
    for (let i = 0; i < Math.min(limit, pool.length); i++) {
      out.push(pool[(cursor + i) % pool.length]);
    }
    return out;
  }, [pool, cursor, limit]);

  // Fire an impression the first time each ad becomes visible.
  useEffect(() => {
    visible.forEach((a) => {
      if (!a?.ad_id || seenRef.current.has(a.ad_id)) return;
      seenRef.current.add(a.ad_id);
      axios.post(`${API}/ads/${a.ad_id}/impression`).catch(() => {});
    });
  }, [visible]);

  const trackClick = (adId) => axios.post(`${API}/ads/${adId}/click`).catch(() => {});

  if (!pool.length || !visible.length) return null;

  if (variant === "banner") {
    const a = visible[0];
    return (
      <a
        href={a.target_url || "#"}
        target={a.target_url ? "_blank" : undefined}
        rel="noopener noreferrer"
        onClick={() => a.target_url && trackClick(a.ad_id)}
        data-testid={`sponsored-ad-banner-${a.ad_id}`}
        className={`group relative flex items-center gap-3 p-2.5 rounded-2xl border border-primary/30 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 hover:from-primary/10 hover:via-primary/20 hover:to-primary/10 transition-all overflow-hidden ${className}`}
      >
        <span className="absolute top-0 right-0 bg-primary text-primary-foreground text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-bl-lg flex items-center gap-1" data-testid={`sponsored-tag-${a.ad_id}`}>
          <Sparkles className="w-3 h-3" /> Patrocinado
        </span>
        {a.video_url ? <AdVideoThumb src={a.video_url} className="w-12 h-12 rounded-xl" /> : a.image_url && (
          <img src={a.image_url} alt="" className="w-12 h-12 rounded-xl object-cover flex-shrink-0" />
        )}
        <div className="min-w-0 flex-1 pr-20">
          <div className="text-sm font-bold truncate">{a.title}</div>
          <div className="text-[11px] text-muted-foreground line-clamp-1">{a.description}</div>
        </div>
        {a.target_url && (
          <span className="hidden sm:inline-flex items-center gap-1 text-xs text-primary font-semibold group-hover:underline shrink-0">
            {a.cta || "Ver"} <ExternalLink className="w-3 h-3" />
          </span>
        )}
      </a>
    );
  }

  if (variant === "compact") {
    return (
      <div className={`space-y-2 ${className}`} data-testid="sponsored-widget-compact">
        {visible.map((a) => (
          <a
            key={a.ad_id}
            href={a.target_url || "#"}
            target={a.target_url ? "_blank" : undefined}
            rel="noopener noreferrer"
            onClick={() => a.target_url && trackClick(a.ad_id)}
            data-testid={`sponsored-ad-compact-${a.ad_id}`}
            className="group flex items-center gap-2 p-2 rounded-lg border border-primary/30 bg-primary/5 hover:bg-primary/10 transition-colors"
          >
            {a.video_url ? <AdVideoThumb src={a.video_url} className="w-10 h-10 rounded-md" /> : a.image_url && (
              <img src={a.image_url} alt="" className="w-10 h-10 rounded-md object-cover flex-shrink-0" />
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1 text-[9px] uppercase tracking-widest text-primary font-bold" data-testid={`sponsored-tag-${a.ad_id}`}>
                <Sparkles className="w-3 h-3" /> Patrocinado
              </div>
              <div className="text-xs font-semibold truncate">{a.title}</div>
            </div>
          </a>
        ))}
      </div>
    );
  }

  if (variant === "inline") {
    return (
      <>
        <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${className}`} data-testid="sponsored-widget-inline">
          {visible.map((a) => (
            <Card
              key={a.ad_id}
              data-testid={`sponsored-ad-inline-${a.ad_id}`}
              className="p-3 flex gap-3 items-center border-primary/40 bg-primary/5 relative overflow-hidden hover-lift"
            >
              <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-bl-lg flex items-center gap-1" data-testid={`sponsored-tag-${a.ad_id}`}>
                <Sparkles className="w-3 h-3" /> Patrocinado
              </div>
              {a.video_url && <AdVideoThumb src={a.video_url} className="w-16 h-16 rounded-lg" />}
              {!a.video_url && a.image_url && (
                <button
                  onClick={() => setZoomSrc(a.image_url)}
                  className="relative group flex-shrink-0"
                  data-testid={`sponsored-zoom-${a.ad_id}`}
                >
                  <img src={a.image_url} alt="" className="w-16 h-16 rounded-lg object-cover" />
                  <span className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center rounded-lg transition-opacity">
                    <Maximize2 className="w-4 h-4 text-white" />
                  </span>
                </button>
              )}
              <div className="min-w-0 flex-1 pr-16">
                <div className="text-sm font-bold truncate">{a.title}</div>
                <div className="text-[11px] text-muted-foreground line-clamp-1">{a.description}</div>
                {a.target_url && (
                  <a
                    href={a.target_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackClick(a.ad_id)}
                    className="inline-flex items-center gap-1 text-[11px] text-primary font-semibold mt-1 hover:underline"
                    data-testid={`sponsored-cta-${a.ad_id}`}
                  >
                    {a.cta || "Saiba mais"} <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </Card>
          ))}
        </div>
        <FullscreenImageViewer src={zoomSrc} onClose={() => setZoomSrc(null)} />
      </>
    );
  }

  // default: card
  return (
    <>
      <div className={`space-y-3 ${className}`} data-testid="sponsored-widget-card">
        {visible.map((a) => (
          <Card
            key={a.ad_id}
            data-testid={`sponsored-ad-${a.ad_id}`}
            className="p-4 border-primary/40 bg-primary/5 relative overflow-hidden hover-lift"
          >
            <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-bl-lg flex items-center gap-1" data-testid={`sponsored-tag-${a.ad_id}`}>
              <Sparkles className="w-3 h-3" /> Patrocinado
            </div>
            <div className="flex gap-3">
              {a.video_url && <AdVideoThumb src={a.video_url} className="w-20 h-20 rounded-lg" />}
              {!a.video_url && a.image_url && (
                <button
                  onClick={() => setZoomSrc(a.image_url)}
                  className="relative group flex-shrink-0"
                  data-testid={`sponsored-zoom-${a.ad_id}`}
                  aria-label="Ampliar"
                >
                  <img src={a.image_url} alt={a.title} className="w-20 h-20 rounded-lg object-cover" />
                  <span className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center rounded-lg transition-opacity">
                    <Maximize2 className="w-4 h-4 text-white" />
                  </span>
                </button>
              )}
              <div className="flex-1 min-w-0 pr-20">
                <div className="text-[10px] text-muted-foreground truncate">por {a.author_name}</div>
                <h4 className="font-bold text-sm leading-tight truncate">{a.title}</h4>
                <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{a.description}</p>
                {a.target_url && (
                  <a
                    href={a.target_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackClick(a.ad_id)}
                    className="inline-flex items-center gap-1 text-xs text-primary font-semibold mt-1.5 hover:underline"
                    data-testid={`sponsored-cta-${a.ad_id}`}
                  >
                    {a.cta || "Saiba mais"} <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
      <FullscreenImageViewer src={zoomSrc} onClose={() => setZoomSrc(null)} />
    </>
  );
}
